import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Eyebrow, FadeUp, MaskedLine } from "@/components/motion";

const ROUNDS = [
  {
    suit: "♠",
    n: "01",
    round: "Round one",
    title: "Take your seat",
    img: "/images/round-one.png",
    alt: "Men talking over drinks at a dark walnut bar in warm lamplight",
    body: "Everyone arrives at the same time and the evening begins in the Lounge. Grab a drink. Meet the other players. Chat, flirt, settle in.",
    note: "Nobody is expected to walk through the door already switched on. The first round is simply about getting comfortable with the men you're going to spend the evening with.",
  },
  {
    suit: "♥",
    n: "02",
    round: "Round two",
    title: "Show your hand",
    img: "/images/round-two.png",
    alt: "Two men relaxing in a warm private lounge, body-positive and at ease",
    body: "Once everyone has arrived and the group has settled, we change gears. Phones are away. Clothes come off. The Lounge becomes a more relaxed, body-positive space where you can see — and be seen — without judgement.",
    note: "Different ages. Different bodies. Different types. There isn't one kind of man who belongs at Players Lounge.",
  },
  {
    suit: "♦",
    n: "03",
    round: "Round three",
    title: "Play when you're ready",
    img: "/images/round-three.png",
    alt: "A dark velvet curtain opening onto warm amber light in a private lounge",
    body: "When the mood takes you, the Lounge's dedicated playspaces are open to you. Join in. Take your time. Watch for a while. Keep chatting. Change your mind.",
    note: "There is no compulsory participation and no expectation that you need to connect sexually with anyone. The only rule is that everybody gets to make their own choices.",
  },
];

const TheGame = () => (
  <div data-testid="the-game-page">
    <section className="lamp-glow mx-auto max-w-7xl px-5 pb-16 pt-36 sm:px-8 sm:pt-44">
      <Eyebrow>How the game works</Eyebrow>
      <h1 className="mt-4 font-serif text-5xl leading-[1.02] tracking-tight text-ivory sm:text-6xl lg:text-7xl">
        <MaskedLine delay={0.1}>One room. One group.</MaskedLine>
        <MaskedLine delay={0.25} className="italic text-brass">
          Three rounds.
        </MaskedLine>
      </h1>
      <FadeUp delay={0.4}>
        <p className="mt-7 max-w-2xl text-lg leading-relaxed text-ivory/70">
          Everyone begins together, and the evening changes gear gradually. That gives you time to
          meet the room before deciding how you want to play.
        </p>
      </FadeUp>
    </section>

    <div className="mx-auto max-w-7xl space-y-24 px-5 pb-24 sm:px-8 sm:space-y-32">
      {ROUNDS.map((r, i) => (
        <section
          key={r.n}
          data-testid={`round-${r.n}`}
          className="grid items-center gap-10 md:grid-cols-2"
        >
          <FadeUp className={i % 2 === 1 ? "md:order-2" : ""}>
            <div className="relative overflow-hidden rounded-2xl border border-brass/15">
              <img src={r.img} alt={r.alt} loading="lazy" className="aspect-[4/3] w-full object-cover" />
              <span className="absolute left-5 top-5 rounded-lg bg-ink/80 px-3 py-1.5 font-serif text-sm italic text-brass backdrop-blur">
                {r.n}
              </span>
            </div>
          </FadeUp>
          <FadeUp delay={0.12} className={i % 2 === 1 ? "md:order-1" : ""}>
            <p className="text-[11px] uppercase tracking-[0.3em] text-brass">
              <span aria-hidden="true" className="mr-2">{r.suit}</span>
              {r.round}
            </p>
            <h2 className="mt-3 font-serif text-4xl text-ivory sm:text-5xl">{r.title}</h2>
            <p className="mt-5 max-w-lg text-base leading-relaxed text-ivory/75">{r.body}</p>
            <p className="mt-4 max-w-lg border-l-2 border-brass/40 pl-5 text-[15px] italic leading-relaxed text-ivory/60">
              {r.note}
            </p>
          </FadeUp>
        </section>
      ))}
    </div>

    <section className="border-t border-brass/10 bg-charcoal/40">
      <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28">
        <FadeUp>
          <p className="font-serif text-3xl italic leading-snug text-ivory sm:text-5xl">
            No pressure. No judgement. <span className="text-brass">No scorecard.</span>
          </p>
          <Link
            to="/the-players"
            data-testid="game-next-players-link"
            className="group mt-10 inline-flex items-center gap-3 rounded-xl border border-brass/30 px-7 py-4 text-sm font-semibold uppercase tracking-[0.2em] text-brass transition-all duration-200 hover:border-brass hover:bg-brass/10"
          >
            Who gets dealt in? — The Players
            <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
        </FadeUp>
      </div>
    </section>
  </div>
);

export default TheGame;
